<template>
  <div>
    <div v-if="studnetN8nFeedback" class="ai-feedback-container">
      <div class="feedback-header">
        <div class="header-content">
          <h3>
            <i class="fa fa-brain"></i>
            {{ $t("aiFeedback.title") }}
          </h3>
          <div class="header-actions">
            <span
              class="last-updated"
              v-if="studnetN8nFeedback.status === 'generated'"
            >
              {{ $t("aiFeedback.lastUpdated") }}:
              {{ formatDate(studnetN8nFeedback.updatedAt) }}
            </span>
            <button
              class="print-button"
              @click="printFeedback"
              :disabled="isPrinting"
              :title="$t('aiFeedback.printReport')"
            >
              <i class="fa fa-print" :class="{ 'fa-spin': isPrinting }"></i>
            </button>
            <button
              class="refresh-button"
              @click="getBulletinFeedback"
              :disabled="isRefreshing"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': isRefreshing }"></i>
            </button>
          </div>
        </div>
      </div>
      <div v-if="isRefreshing || studnetN8nFeedback.status === 'initiated'">
        <a-alert
          v-if="studnetN8nFeedback.status === 'initiated'"
          type="info"
          class="mb-3"
          :message="$t('aiFeedback.initiatedMessage')"
          :description="$t('aiFeedback.initiatedDescription')"
        />
        <a-skeleton avatar :paragraph="{ rows: 3 }" active />
        <a-skeleton avatar :paragraph="{ rows: 3 }" active />
        <a-skeleton avatar :paragraph="{ rows: 3 }" active />
      </div>
      <div v-else class="feedback-content">
        <div class="summary-section feedback-card">
          <div class="section-icon info">
            <i class="fa fa-file-text"></i>
          </div>
          <div class="section-content">
            <h4>{{ $t("aiFeedback.summary") }}</h4>
            <p>
              {{ getLocalizedContent(studnetN8nFeedback.feedback.summary) }}
            </p>
          </div>
        </div>

        <div class="strengths-section feedback-card">
          <div class="section-icon success">
            <i class="fa fa-thumbs-up"></i>
          </div>
          <div class="section-content">
            <h4>{{ $t("aiFeedback.strengths") }}</h4>
            <ul>
              <li
                v-for="(strength, index) in getLocalizedContent(
                  studnetN8nFeedback.feedback.strengths
                )"
                :key="index"
              >
                <i class="fa fa-plus-circle"></i>
                {{ strength }}
              </li>
            </ul>
          </div>
        </div>

        <div class="weaknesses-section feedback-card">
          <div class="section-icon warning">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <div class="section-content">
            <h4>{{ $t("aiFeedback.weaknesses") }}</h4>
            <ul>
              <li
                v-for="(weakness, index) in getLocalizedContent(
                  studnetN8nFeedback.feedback.weaknesses
                )"
                :key="index"
              >
                <i class="fa fa-minus-circle"></i>
                {{ weakness }}
              </li>
            </ul>
          </div>
        </div>

        <div class="recommendations-section feedback-card">
          <div class="section-icon primary">
            <i class="fa fa-bullseye"></i>
          </div>
          <div class="section-content">
            <h4>{{ $t("aiFeedback.recommendations") }}</h4>
            <ul>
              <li
                v-for="(recommendation, index) in getLocalizedContent(
                  studnetN8nFeedback.feedback.recommendations
                )"
                :key="index"
              >
                <i class="fa fa-angle-right"></i>
                {{ recommendation }}
              </li>
            </ul>
          </div>
        </div>

        <div class="appreciation-section feedback-card">
          <div class="section-icon info">
            <i class="fa fa-star"></i>
          </div>
          <div class="section-content">
            <h4>{{ $t("aiFeedback.generalAppreciation") }}</h4>
            <p>
              {{
                getLocalizedContent(studnetN8nFeedback.feedback.appreciations)
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { pdfHeader } from "@/utilities/pdfUtils";
import moment from "moment";

export default {
  name: "AIFeedback",
  props: {
    type: {
      type: String,
      required: true,
    },
    selectedStudent: {
      type: [String, Number],
      required: true,
    },
    selectedTrimester: {
      type: [String, Number],
      required: true,
    },
    studnetN8nFeedback: {
      type: Object,
      required: true,
    },
    studentName: {
      type: Object,
      default: () => ({}),
    },
    className: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isRefreshing: false,
      isPrinting: false,
      pollingInterval: null,
    };
  },
  mounted() {
    this.pollingInterval = setInterval(() => {
      if (this.studnetN8nFeedback?.status === "initiated")
        this.getBulletinFeedback();
    }, 5000);
  },
  beforeDestroy() {
    // Clean up the interval when component is destroyed
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  computed: mapState(["settings"]),
  methods: {
    formatDate(date) {
      return moment(date).format("dddd D MMMM YYYY [à] HH:mm", {
        userLocale: moment.locale("fr-tn"),
      });
    },
    getLocalizedContent(content) {
      if (!content) return "";
      const locale = this.$i18n.locale.substring(0, 2);
      return content[locale] || content.en; // Fallback to English if translation not available
    },
    getBulletinFeedback() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      this.$emit("refresh");
      this.isRefreshing = false;
    },
    async printFeedback() {
      if (this.isPrinting) return;
      this.isPrinting = true;

      try {
        const doc = pdfHeader(this.settings, {
          title: this.$t("aiFeedback.title")
        });

        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 20;
        const contentWidth = pageWidth - margin * 2;
        let yPosition = 40; // Increased starting position to account for header
        doc.setFont("Amiri");

        // Reset text color for content
        doc.setTextColor(0, 0, 0);

        // Student Information Section
        yPosition = this.addStudentInfoSection(
          doc,
          yPosition,
          margin,
          contentWidth
        );

        // Divider line
        yPosition += 8;
        doc.setDrawColor(79, 70, 229);
        doc.setLineWidth(0.5);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 12;

        // Summary Section with improved styling
        yPosition = this.addStyledSection(
          doc,
          this.$t("aiFeedback.summary"),
          this.getLocalizedContent(this.studnetN8nFeedback.feedback.summary),
          yPosition,
          margin,
          contentWidth,
          [79, 70, 229]
        );

        // Strengths Section
        const strengths = this.getLocalizedContent(
          this.studnetN8nFeedback.feedback.strengths
        );
        yPosition = this.addStyledListSection(
          doc,
          this.$t("aiFeedback.strengths"),
          strengths,
          yPosition,
          margin,
          contentWidth,
          [22, 163, 74]
        );

        // Weaknesses Section
        const weaknesses = this.getLocalizedContent(
          this.studnetN8nFeedback.feedback.weaknesses
        );
        yPosition = this.addStyledListSection(
          doc,
          this.$t("aiFeedback.weaknesses"),
          weaknesses,
          yPosition,
          margin,
          contentWidth,
          [217, 119, 6]
        );

        // Recommendations Section
        const recommendations = this.getLocalizedContent(
          this.studnetN8nFeedback.feedback.recommendations
        );
        yPosition = this.addStyledListSection(
          doc,
          this.$t("aiFeedback.recommendations"),
          recommendations,
          yPosition,
          margin,
          contentWidth,
          [37, 99, 235]
        );

        // Appreciations Section
        yPosition = this.addStyledSection(
          doc,
          this.$t("aiFeedback.generalAppreciation"),
          this.getLocalizedContent(
            this.studnetN8nFeedback.feedback.appreciations
          ),
          yPosition,
          margin,
          contentWidth,
          [124, 58, 237]
        );

        // Footer
        yPosition = this.addFooter(doc, yPosition, margin, contentWidth);

        // Save the PDF
        const studentName = this.studentName
          .replace(/[^a-zA-Z0-9\s]/g, "")
          .replace(/\s+/g, "_");
        const fileName = `AI_Feedback_${studentName}_${this.selectedTrimester}.pdf`;
        doc.save(fileName);

        // Show success message
        this.$message.success(this.$t("aiFeedback.pdfGeneratedSuccessfully"));
      } catch (error) {
        this.$message.error(this.$t("aiFeedback.pdfGenerationError"));
      } finally {
        this.isPrinting = false;
      }
    },

    addStudentInfoSection(doc, yPosition, margin, contentWidth) {
      const pageHeight = doc.internal.pageSize.height;

      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = 20;
      }

      // Calculate text heights first
      doc.setFontSize(14);
      const titleHeight = 10;

      doc.setFontSize(11);
      const studentName = this.studentName;
      const trimester = this.selectedTrimester;
      const classroom = this.className;
      const date = this.formatDate(this.studnetN8nFeedback.updatedAt);

      // Calculate total content height
      const lineHeight = 8;
      const contentHeight = 5 * lineHeight + titleHeight + 20; // 5 lines + title + padding

      // Student info background with adjusted height
      doc.setFillColor(249, 250, 251);
      doc.rect(margin, yPosition - 5, contentWidth, contentHeight, "F");

      // Border
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.rect(margin, yPosition - 5, contentWidth, contentHeight);

      // Student info title
      doc.setFontSize(14);
      doc.setTextColor(26, 31, 54);
      doc.text(
        `${this.$t("aiFeedback.information")} `,
        margin + 10,
        yPosition + 8
      );
      doc.setFontSize(10);
      doc.text(
        `${this.$t("bulletin.bulletinPedagogique")}`,
        margin + 10,
        yPosition + 13
      );

      // Student details with adjusted spacing
      doc.setFontSize(11);
      doc.setTextColor(75, 85, 99);

      let currentY = yPosition + 25; // Start position for details
      const leftPadding = margin + 10;

      doc.text(
        `${this.$t("bulletin.nom")}: ${studentName}`,
        leftPadding,
        currentY
      );
      currentY += lineHeight + 4;
      doc.text(
        `${this.$t("emploi.trimestre")}: ${trimester}`,
        leftPadding,
        currentY
      );
      currentY += lineHeight + 4;
      doc.text(
        `${this.$t("emploi.classe")}: ${classroom}`,
        leftPadding,
        currentY
      );
      currentY += lineHeight + 4;
      doc.text(
        `${this.$t("aiFeedback.generatedAt")}: ${date}`,
        leftPadding,
        currentY
      );

      return yPosition + contentHeight + 5;
    },

    addStyledSection(
      doc,
      title,
      content,
      yPosition,
      margin,
      contentWidth,
      color
    ) {
      const pageHeight = doc.internal.pageSize.height;

      if (yPosition > pageHeight - 50) {
        doc.addPage();
        yPosition = 20;
      }

      // Calculate content height first
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(content, contentWidth - 20);
      const contentHeight = lines.length * 7;
      const totalHeight = Math.max(40, contentHeight + 25); // Increased minimum height

      // Section background
      doc.setFillColor(255, 255, 255);
      doc.rect(margin, yPosition - 3, contentWidth, totalHeight, "F");

      // Section border with color
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(1);
      doc.rect(margin, yPosition - 3, contentWidth, totalHeight);

      // Section title with color
      doc.setFontSize(14);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.text(title, margin + 10, yPosition + 12);

      // Section content
      doc.setFontSize(11);
      doc.setTextColor(75, 85, 99);
      doc.text(lines, margin + 10, yPosition + 25);

      return yPosition + totalHeight + 10;
    },

    addStyledListSection(
      doc,
      title,
      items,
      yPosition,
      margin,
      contentWidth,
      color
    ) {
      const pageHeight = doc.internal.pageSize.height;

      if (yPosition > pageHeight - 70) {
        doc.addPage();
        yPosition = 20;
      }

      // Calculate total height needed
      let totalHeight = 35; // Increased initial height for title + padding
      items.forEach((item) => {
        const lines = doc.splitTextToSize(`• ${item}`, contentWidth - 30);
        totalHeight += lines.length * 7 + 5;
      });

      // Section background
      doc.setFillColor(255, 255, 255);
      doc.rect(margin, yPosition - 3, contentWidth, totalHeight, "F");

      // Section border with color
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(1);
      doc.rect(margin, yPosition - 3, contentWidth, totalHeight);

      // Section title with color
      doc.setFontSize(14);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.text(title, margin + 10, yPosition + 12);

      // List items
      doc.setFontSize(11);
      doc.setTextColor(75, 85, 99);

      let currentY = yPosition + 25; // Increased starting position for items
      items.forEach((item, index) => {
        // Check if we need a new page for this item
        if (currentY > pageHeight - 25) {
          doc.addPage();
          currentY = 20;
        }

        const bulletPoint = `• ${item}`;
        const lines = doc.splitTextToSize(bulletPoint, contentWidth - 30);
        doc.text(lines, margin + 15, currentY);
        currentY += lines.length * 7 + 5;
      });

      return yPosition + totalHeight + 10;
    },

    addFooter(doc, yPosition, margin, contentWidth) {
      const pageHeight = doc.internal.pageSize.height;

      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = pageHeight - 40;
      }

      // Footer line
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(
        margin,
        yPosition,
        doc.internal.pageSize.width - margin,
        yPosition
      );
      yPosition += 10;

      // Footer text
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text("Generated by AI", doc.internal.pageSize.width / 2, yPosition, {
        align: "center",
      });

      // Page number
      const pageCount = doc.internal.getNumberOfPages();
      doc.text(
        `Page ${pageCount}`,
        doc.internal.pageSize.width - margin,
        yPosition,
        { align: "right" }
      );

      return yPosition + 15;
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-feedback-container {
  padding: 4px;
  background: #ffffff;

  .feedback-header {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f2f5;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        margin: 0;
        color: #1a1f36;
        font-size: 1.5rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #4f46e5;
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 16px;

        .last-updated {
          color: #6b7280;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .print-button {
          background: none;
          border: none;
          padding: 8px;
          cursor: pointer;
          color: #4f46e5;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            background: #f3f4f6;
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          i {
            font-size: 1.1rem;
          }
        }

        .refresh-button {
          background: none;
          border: none;
          padding: 8px;
          cursor: pointer;
          color: #4f46e5;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            background: #f3f4f6;
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          i {
            font-size: 1.1rem;
          }
        }
      }
    }
  }

  .feedback-content {
    display: grid;
    gap: 24px;

    .feedback-card {
      display: flex;
      gap: 20px;
      padding: 24px;
      background: #ffffff;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
      }

      .section-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        flex-shrink: 0;

        &.success {
          background: #dcfce7;
          color: #16a34a;
        }

        &.warning {
          background: #fef3c7;
          color: #d97706;
        }

        &.primary {
          background: #e0e7ff;
          color: #4f46e5;
        }

        &.info {
          background: #dbeafe;
          color: #2563eb;
        }
      }

      .section-content {
        flex: 1;

        h4 {
          color: #1a1f36;
          margin: 0 0 16px 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        p {
          color: #4b5563;
          line-height: 1.6;
          margin: 0;
        }

        ul {
          list-style-type: none;
          padding: 0;
          margin: 0;

          li {
            padding: 12px 0;
            color: #4b5563;
            display: flex;
            align-items: flex-start;
            gap: 12px;
            line-height: 1.5;

            i {
              color: #4f46e5;
              margin-top: 4px;
            }

            &:not(:last-child) {
              border-bottom: 1px solid #f3f4f6;
            }
          }
        }
      }
    }
  }

  .no-feedback {
    text-align: center;
    color: #6b7280;
    padding: 48px 24px;
    background: #f9fafb;
    border-radius: 12px;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    i {
      font-size: 1.5rem;
      color: #9ca3af;
    }
  }
}
</style>
